import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TimeGreeting } from '../components/home/TimeGreeting';
import MorningDashboard from '../components/home/MorningDashboard';
import { WellnessTracker } from '../components/home/WellnessTracker';
import { Schedule } from '../components/schedule/Schedule';
import { FocusHub } from '../components/focus/FocusHub';
import { productivityService } from '../services/productivity/productivity';
import { Task, FocusSession, DailyLog } from '../services/productivity/types';
import { toast } from 'sonner';
import { ListTodo, Calendar, Target } from 'lucide-react';
import { Dock, DockIcon, DockItem, DockLabel } from '@/components/ui/dock';

type Tab = 'tasks' | 'schedule' | 'focus';

const Home = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [focusSessions, setFocusSessions] = useState<FocusSession[]>([]);
  const [dailyLog, setDailyLog] = useState<DailyLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('tasks');
  const [isAgendaLocked, setIsAgendaLocked] = useState(true);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        setIsAgendaLocked(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksData, sessionsData, logData] = await Promise.all([
        productivityService.getTasks(),
        productivityService.getFocusSessions(),
        productivityService.getDailyLog(new Date())
      ]);

      setTasks(tasksData);
      setFocusSessions(sessionsData);
      setDailyLog(logData);
      setIsLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
      setIsLoading(false);
    }
  };

  const handleTaskUpdate = async (taskId: string, updates: Partial<Task>) => {
    try {
      await productivityService.updateTask(taskId, updates);
      await loadData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    try {
      await productivityService.deleteTask(taskId);
      await loadData();
      toast.success('Tarefa excluída com sucesso!');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Erro ao excluir tarefa');
    }
  };

  const handleTaskStatusChange = async (taskId: string, status: 'todo' | 'done') => {
    try {
      await productivityService.updateTaskStatus(taskId, status);
      await loadData();
      toast.success(status === 'done' ? 'Tarefa concluída!' : 'Tarefa reaberta!');
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Erro ao atualizar status da tarefa');
    }
  };

  const handleTaskCreate = async (task: Omit<Task, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
    try {
      await productivityService.createTask(task);
      await loadData();
      toast.success('Tarefa criada com sucesso!');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Erro ao criar tarefa');
    }
  };

  const handleSessionStart = async (session: Omit<FocusSession, 'id'>) => {
    try {
      await productivityService.startFocusSession(session);
      await loadData();
      toast.success('Sessão de foco iniciada!');
    } catch (error) {
      console.error('Error starting focus session:', error);
      toast.error('Erro ao iniciar sessão de foco');
    }
  };

  const handleTabChange = (tab: 'tasks' | 'schedule' | 'focus') => {
    if (tab === 'schedule' && isAgendaLocked) {
      return;
    }
    setActiveTab(tab);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <ListTodo className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden">
      <div className="relative z-10">
        <TimeGreeting />
        
        <div className="container mx-auto px-0">
          <div className="flex-1 overflow-auto">
            <AnimatePresence mode="wait">
              {activeTab === 'tasks' && (
                <motion.div
                  key="tasks"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <MorningDashboard
                    tasks={tasks}
                    dailyLog={dailyLog}
                    onTaskCreate={handleTaskCreate}
                    onTaskUpdate={handleTaskUpdate}
                    onTaskDelete={handleTaskDelete}
                    onTaskStatusChange={handleTaskStatusChange}
                  />
                  <WellnessTracker dailyLog={dailyLog} />
                </motion.div>
              )}

              {activeTab === 'schedule' && (
                <motion.div
                  key="schedule"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Schedule />
                </motion.div>
              )}

              {activeTab === 'focus' && (
                <motion.div
                  key="focus"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <FocusHub focusSessions={focusSessions} onSessionStart={handleSessionStart} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Dock Navigation */}
      <div className="fixed bottom-8 left-0 right-0 z-50">
        <Dock className="dock-shadow">
          <DockItem
            onClick={() => handleTabChange('tasks')}
            className="dock-item"
            data-active={activeTab === 'tasks'}
          >
            <DockLabel className="dock-label">Tarefas</DockLabel>
            <DockIcon>
              <ListTodo className="h-full w-full text-white" />
            </DockIcon>
          </DockItem>

          <DockItem
            onClick={() => handleTabChange('schedule')}
            className={`dock-item ${isAgendaLocked ? 'locked' : ''}`}
            data-active={activeTab === 'schedule'}
          >
            <DockLabel className="dock-label">Agenda</DockLabel>
            <DockIcon>
              <Calendar className="h-full w-full text-white" />
            </DockIcon>
          </DockItem>

          <DockItem
            onClick={() => handleTabChange('focus')}
            className="dock-item"
            data-active={activeTab === 'focus'}
          >
            <DockLabel className="dock-label">Foco</DockLabel>
            <DockIcon>
              <Target className="h-full w-full text-white" />
            </DockIcon>
          </DockItem>
        </Dock>
      </div>
    </div>
  );
};

export default Home;
